<template>
  <AttachmentsList
    v-if="attachments.length"
    :model="props.model?.attachmentList"
    class="attachments-container"
    :items="attachments"
    mode="composer"
    size="sm"
    align="left"
    :show-remove="true"
    :dedupeKey="
      (f: { name: any; size: any; lastModified: any }) => `${f.name}-${f.size}-${f.lastModified}`
    "
    @remove="removeAttachment"
  />
  <div :class="['form-input', { 'is-disabled': isDisabled }]">
    <Button
      :model="{
        path: '',
        base64: appStore.currentMode() === DisplayMode.Light ? iconPlusLight : iconPlusDark,
        url: '',
      }"
      :alt="t('chat.question.addAttachmentButton.alt')"
      :click="openFileDialog"
      :isDisabled="addDisabled || isDisabled"
      :key="componentKey"
      :text="
        addDisabled
          ? t('supportRequest.fields.attachment.buttonTextMaxAttachments')
          : t('supportRequest.fields.attachment.buttonText')
      "
    ></Button>
  </div>
  <input type="file" ref="fileInput" class="hidden" @change="onFileInput" multiple />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { t } from '../../composables/Translation'
import Button from '../Button.vue'
import AttachmentsList from '../AttachmentsList.vue'
import { updateComponentKey } from '../../composables/ComponentKey'
import { useAttachments } from '../../composables/AttachmentsValidation'
import type { AttachmentsInput } from '../../types/configuration/AttachmentsInput'
import { useToast } from '../../composables/useToast'
import iconPlusLight from '../../assets/icons/light/icon-plus.svg'
import iconPlusDark from '../../assets/icons/dark/icon-plus.svg'
import { DisplayMode } from '../../types/app/displayMode'
import { useAppStore } from '../../stores/appStore'

const appStore = useAppStore()

const props = defineProps<{
  model: AttachmentsInput | undefined
  id: string
  multiple?: boolean
  modelValue: File[]
  label: string
  isDisabled: boolean
  chatPdfBytes?: number
}>()

const maximumBytesTotal = computed(() => {
  const max = props.model?.attachmentLimitations?.maximumBytesTotal
  const used = props.chatPdfBytes ?? 0
  return max != null ? Math.max(0, max - used) : undefined
})

const {
  attachments,
  addDisabled,
  error: attachError,
  tryAdd,
  clear,
  removeByKey,
} = useAttachments({
  maximumAttachments: props.model?.attachmentLimitations.maximumAttachments,
  maximumBytesTotal: maximumBytesTotal,
  maximumBytesPerFile: props.model?.attachmentLimitations.maximumBytesPerFile,
  acceptedExtensions: props.model?.attachmentLimitations.acceptedExtensions,
  acceptedMime: props.model?.attachmentLimitations.acceptedMime,
  excludedExtensions: props.model?.attachmentLimitations.excludedExtensions,
  excludedMime: props.model?.attachmentLimitations.excludedMime,
})

watch(
  () => props.modelValue,
  nv => {
    if (!nv || nv.length === 0) {
      clear()
      if (fileInput.value) fileInput.value.value = ''
    }
  }
)

watch(attachError, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    useToast().error(newVal)
    attachError.value = ''
  }
})

const fileInput = ref<HTMLInputElement | null>(null)

const isUrlValid = ref(false)
let componentKey = updateComponentKey(
  props,
  () => (isUrlValid.value = props.modelValue !== undefined)
)

function openFileDialog() {
  fileInput.value?.click()
}

const keyOf = (f: File) => `${f.name}-${f.size}-${f.lastModified}`

function removeAttachment(key: string) {
  removeByKey(key)

  const i = props.modelValue.findIndex(f => keyOf(f) === key)
  if (i !== -1) props.modelValue.splice(i, 1)
}

function onFileInput(e: Event) {
  const input = e.target as HTMLInputElement
  const files = input.files
  if (!files) return
  const { accepted } = tryAdd(files)
  const f: File[] = accepted.map(a => a.file).filter((f): f is File => !!f)

  props.modelValue.push(...f)
  input.value = ''
}
</script>

<style scoped>
.hidden {
  display: none;
}

.form-input {
  border: none;
  outline: none;
  box-shadow: none;
  color: var(--light-color);
  font: inherit;
  font-size: 14px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  padding: 12px;
  width: 100%;
  box-sizing: border-box;
}

.attachments-container {
  margin-bottom: 16px;
}

.is-disabled {
  background-color: #f5f6f8;
  color: #a0a4ad;
  cursor: not-allowed;
}
</style>
